const MAX_LENGTH = 128_000;
const MAX_CANDIDATES = 128;

export class ModelResponseFormatError extends Error {
  constructor() {
    super("返回内容不完整，请重新生成。");
    this.name = "ModelResponseFormatError";
  }
}

/** Keep clearly broken structured output out of games' plain-text fallbacks. */
export function normalizeModelResponseText(raw: string): string {
  const repaired = repairModelJsonText(raw);
  const body = repaired.trim().replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "");
  if (/^(?:\{\s*(?:"|$)|\[\s*(?:\{|"|$))/.test(body) && !isJson(body)) {
    throw new ModelResponseFormatError();
  }
  return repaired;
}

function isJson(value: string): boolean {
  try { JSON.parse(value); return true; } catch { return false; }
}

/** Repair only complete model output, never HTTP/SSE envelopes or partial deltas.
 * Valid JSON and ordinary prose are byte-for-byte unchanged. No missing braces,
 * invented fields, inner-quote guessing, or model calls. Callers still validate
 * their own game schema. Ambiguous repairs are rejected.
 */
export function repairModelJsonText(raw: string): string {
  if (raw.length > MAX_LENGTH) return raw;
  const fence = /^(\s*```(?:json)?\s*\n)([\s\S]*?)(\n\s*```\s*)$/i.exec(raw);
  const body = fence ? fence[2] : raw;
  const trimmed = body.trim();
  if (!/^(?:\{[\s\S]*\}|\[[\s\S]*\])$/.test(trimmed) || isJson(body)) return raw;
  const wrap = (fixed: string) => fence ? fence[1] + fixed + fence[3] : fixed;

  // Trailing commas may be removed only outside strings.
  let quoted = false;
  let escaped = false;
  let withoutTrailingCommas = "";
  for (let i = 0; i < body.length; i++) {
    const char = body[i];
    if (quoted) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') quoted = false;
    } else {
      if (char === '"') quoted = true;
      if (char === "," && /^\s*[}\]]/.test(body.slice(i + 1))) continue;
    }
    withoutTrailingCommas += char;
  }
  if (withoutTrailingCommas !== body && isJson(withoutTrailingCommas)) return wrap(withoutTrailingCommas);

  // One missing closing quote immediately before a structural delimiter.
  // JSON.parse proves the whole candidate is valid; a second valid location
  // makes the repair ambiguous, so retain the original for the game's fallback.
  const positions = [...body.matchAll(/[}\],]/g)];
  if (positions.length > MAX_CANDIDATES) return raw;
  let repaired: string | undefined;
  for (const match of positions) {
    const index = match.index!;
    const candidate = body.slice(0, index) + '"' + body.slice(index);
    if (!isJson(candidate)) continue;
    if (repaired !== undefined) return raw;
    repaired = candidate;
  }
  return repaired === undefined ? raw : wrap(repaired);
}
